import { useSelector } from 'react-redux';
import { FormatTypes, format } from '../../Lib/Format';
import webApi from '../../Lib/WebApi';
import { Button, CheckboxGroup, Field, RadioGroup, TextOutput } from '../Core';
import { ChartFieldSection, GridFieldSection, ListFieldSection, PageSection, Table } from '../Layout';

const FilterSection = () => {
  const products = useSelector((state) =>
    state.admin.syncModel.products?.map((p, i) => ({
      name: `syncParams.licenseUsageFilter.products[${i}]`,
      label: p.Name,
      disabled: !p.Licensed,
    }))
  );

  return (
    <ListFieldSection>
      {products ? <Field input={<CheckboxGroup />} label="Products" options={products} /> : 'Loading products...'}

      <Field
        input={<RadioGroup />}
        label="Usage from Last"
        name="syncParams.licenseUsageFilter.period"
        options={[
          { label: '7 days', value: 'SevenDay' },
          { label: '1 month', value: 'OneMonth' },
          { label: '6 months', value: 'SixMonth' },
        ]}
      />
    </ListFieldSection>
  );
};

const UsageGridSection = () => {
  const filterPeriod = useSelector((state) => state.admin.syncParams.licenseUsageFilter.period);
  const selectedProducts = useSelector((state) => state.admin.syncParams.licenseUsageFilter.products) ?? [];

  const products = useSelector((state) => state.admin.syncModel.products);
  const usage = useSelector((state) => state.admin.syncModel.usage?.LicenseProductUsage);
  const periodStart = useSelector((state) => state.admin.syncModel.usage?.StartTrackingPeriod);
  const periodEnd = useSelector((state) => state.admin.syncModel.usage?.EndTrackingPeriod);
  const period =
    format({ type: FormatTypes.Date })(periodStart) + ' to ' + format({ type: FormatTypes.Date })(periodEnd);

  //Used to lookup the Peak Usage for a given Product
  const peakUsage = usage?.reduce(
    (lookup, peakUsage) => ({ [peakUsage.Name]: peakUsage.MaxLicenseUsage, ...lookup }),
    {}
  );

  const getGridData = () => {
    if (!products) return [];

    return products
      .filter((_, i) => !!selectedProducts[i])
      .map(({ Name, LicensedUserCount }) => [
        <Field input={<TextOutput />} value={Name} />,
        <Field input={<TextOutput />} value={peakUsage ? peakUsage[Name] : ''} />,
        <Field input={<TextOutput />} value={LicensedUserCount || (Name === 'QESTLab' ? 'Unlimited' : '0')} />,
      ]);
  };

  const showGrid = products?.some((_, i) => !!selectedProducts[i]) && peakUsage;

  const csvExportUri = () => {
    if (!products?.some((_, i) => !!selectedProducts[i])) return null;
    let productsToDisplay = products.map((p, i) => (!!selectedProducts[i] ? p.Name : null)).filter((p) => !!p);
    //Replace "/" with "-" for date time to avoid issues with the csv filename
    //Still use default date format to get the correct locale date format
    //(i.e. DD/MM/YYYY for UK, MM/DD/YYYY for US, etc.)
    var filename =
      'qestplatform_license_usage_' +
      format({ type: FormatTypes.Date })(periodStart).replaceAll('/', '-') +
      '_' +
      format({ type: FormatTypes.Date })(periodEnd).replaceAll('/', '-') +
      '.csv';

    return webApi.admin.license.usage.csv.buildUri({
      filename,
      Products: productsToDisplay,
      PeriodToReview: filterPeriod,
    });
  };

  return showGrid ? (
    <>
      <ListFieldSection heading="Peak Concurrent License Usage" single wide>
        <Field input={<TextOutput />} label="Period" value={period} />
      </ListFieldSection>
      <GridFieldSection>
        <Table
          colHeaders={['Product', 'Peak Usage', 'Total Licenses']}
          colWidths={[200, 150, 150]}
          data={getGridData()}
          alternateRowShading={true}
        />
      </GridFieldSection>
      <span>
        <Button primary href={csvExportUri()}>
          Download CSV
        </Button>
      </span>
    </>
  ) : null;
};

const LicenseUsageAdmin = () => {
  const chart = useSelector((state) => state.admin.syncModel.usage?.Chart);

  return (
    <PageSection>
      <FilterSection />
      <ChartFieldSection chart={chart} wide />
      <UsageGridSection />
    </PageSection>
  );
};

export default LicenseUsageAdmin;
export { LicenseUsageAdmin };
